import * as React from "react";
import { useState, useEffect } from "react";
import classNames from "classnames";
import { withStyles } from "@material-ui/core";

const styles = {
  text: {
    width: "100%",
    height: "min-content",
    fontSize: "13px",
    fontFamily: "inherit",
    backgroundColor: "unset",
    outlineStyle: "none" as any,
    color: "#666",
    border: "0",
    padding: 0,
    margin: 0,
    lineHeight: "1rem",
  },
  textarea: {
    width: "100%",
    fontSize: "13px",
    fontFamily: "inherit",
    backgroundColor: "unset",
    border: "none!important",
    verticalAlign: "inherit",
    resize: "none" as any,
    overflow: "hidden",
    "&:focus": {
      outline: "none",
    },
  },
  scrollOnFocus: {
    "&:focus": {
      overflow: "unset",
    },
  },
};

interface IProps
  extends React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  classes: any;
  /** Habilita/desabilita o textarea para alterar a descrição. */
  selected?: boolean;
  /** Habilita/Desabilita as operações para ajustar a altura do
   * componente ao texto informado.
   */
  enableResizeHeight?: boolean;
  /** Índice da linha em que a célula pertence. */
  rowIndex: number;
  /** Altura padrão para célula, também usada como altura mínima do componente. */
  defaultRowHeight?: number;
  /** Evento disparado ao obter a referência ao elemento DOM do textarea. */
  onGetRef?: (element: HTMLTextAreaElement) => void;
  /** Valor qualquer que ao ser alterado, dispara o rook para resetar a altura do textarea
   * de volta ao valor padrão.
   */
  forceUpdate?: any;
}

/** Este componente renderiza um elemento textarea ajustando sua altura de acordo com o texto
 * e/ou o estilo definido para o mesmo.
 */
const DescriptionCellTask = ({
  classes,
  id,
  selected,
  rowIndex,
  style,
  className,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  enableResizeHeight = true,
  defaultRowHeight = 24,
  onGetRef = () => {},
  forceUpdate = undefined,
  ...props
}: IProps) => {
  const [textHeight, setTextHeight] = useState(defaultRowHeight);
  const [textareaRef, setTextAreaRef] = useState<HTMLTextAreaElement | null>(
    null
  );
  const [focus, setFocus] = useState(false);
  const [reset, setReset] = useState(false);

  /**
   * Força a altura a voltar para o valor inicial e com isso 'redefinir' a altura correta, pois
   * a altura é obtida com a propriedade 'scrollHeight' que não volta para o tamanho menor
   * em todos os casos.
   */
  useEffect(() => {
    if (textHeight >= defaultRowHeight) {
      setReset(true);
    }
  }, [forceUpdate]);

  /** Atualiza a altura do campo quando receber a referência do elemento DOM,
   * quando alterar os valores de value e/ou defaultValue, e
   * quando alterar a reset para executar o forceUpdate.
   */
  useEffect(() => {
    if (!enableResizeHeight || !textareaRef) return;

    // Se o campo estiver em foco entao só altera a altura pelo evento de onKeyDown
    if (focus) return;

    let seconds = 0;
    if (reset) {
      seconds = 500;
      setTextHeight(defaultRowHeight);
      setReset(false);
    }

    setTimeout(() => {
      const description = getText(value || defaultValue);
      setHeightForDescriptionLength(description);
    }, seconds);
  }, [reset, textareaRef, value, defaultValue]);

  const handleFocus = (ev) => {
    if (onFocus) onFocus(ev);
    setFocus(true);
  };

  const handleBlur = (ev) => {
    if (onBlur) onBlur(ev);
    setFocus(false);
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setHeightForDescriptionLength(newValue);

    if (onChange) onChange(event);
  };

  const handleRef = (el: any) => {
    if (el && el instanceof HTMLTextAreaElement) {
      if (el !== textareaRef) {
        setTextAreaRef(el);
        onGetRef(el);
      }
    }
  };

  /** Calcula a nova altura da linha de acordo com a descrição informada.
   * Retornando o novo valor pela função callback onReasizeHeight.
   */
  function setHeightForDescriptionLength(description: string) {
    let newHeight = defaultRowHeight;
    description = String(description || "");

    if (description && textareaRef) {
      newHeight =
        textareaRef.scrollHeight < defaultRowHeight
          ? defaultRowHeight
          : textareaRef.scrollHeight;
    }

    // Se a linha estiver em alteração mantém o valor mais alto
    if (focus && newHeight < (textHeight || 0)) {
      return;
    }

    if (textHeight !== newHeight && textareaRef) {
      setTextHeight(newHeight);
    }
  }

  /** Trata os possíveis valores passados para um textarea, transformando-os
   * em string.
   */
  function getText(text: string | number | string[] = ""): string {
    if (Array.isArray(text)) return text.join();
    return String(text);
  }

  if (selected) {
    if (!enableResizeHeight) {
      // Usa o textarea como se fosse input
      return (
        <textarea
          {...props}
          id={id}
          ref={handleRef}
          className={classNames(classes.textarea, className)}
          value={value}
          defaultValue={defaultValue}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          rows={1}
          wrap="hard"
          style={{
            ...style,
            height: defaultRowHeight,
            minHeight: defaultRowHeight,
          }}
        />
      );
    } else {
      return (
        <textarea
          {...props}
          id={id}
          ref={handleRef}
          className={classNames(classes.textarea, className)}
          value={value}
          defaultValue={defaultValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={onChange}
          rows={1}
          wrap="hard"
          style={{
            ...style,
            height: textHeight,
            minHeight: textHeight,
          }}
        />
      );
    }
  }

  return (
    <p
      id={id}
      ref={handleRef}
      className={classNames(classes.text, className)}
      style={{
        ...style,
        minHeight: textHeight,
      }}
    >
      {value || defaultValue}
    </p>
  );
};

export default withStyles(styles)(DescriptionCellTask);



// WEBPACK FOOTER //
// ./src/modules/WBS/components/layouts/DescriptionCellTask.tsx